import {FormState, FormStateWithIssues} from '@raison/form-models';
import {RsnButton, RsnDialog, RsnInput, RsnTextButton} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {useReducer} from 'react';
import {useNavigate} from 'react-router';
import {GenericErrorMsg} from '~/entities/dialogs';
import {sessionUpdated, UserStatus} from '~/entities/user';
import {AttachWalletErrorCode, useAttachWalletMutation} from '~/entities/user/api/attachWallets';
import {useLocationState} from '~/library/navigation';
import {InvalidPasswordDialog} from '~/processes/onboarding/ui/kyc/InvalidPasswordDialog';
import {appLocalStorage, appSessionStorage} from '~/shared/persistence';
import {useAppDispatch} from '~/shared/store';
import {WalletsCreatedDialog} from './WalletsCreatedDialog';

export function ConfirmPassword() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [form, dispatchForm] = useReducer(
        FormState.reduce<ConfirmPasswordValued>,
        {
            password: '',
        },
        FormState.from
    );

    const formWithIssues = FormStateWithIssues.fromRecipe(form, issues => {
        if (!form.fields.password.value) {
            issues.password = 'This field is required';
        }
    });

    const {mutateAsync} = useAttachWalletMutation();

    const [receivedStatus, setReceivedStatus] = React.useState<UserStatus | null>(null);
    const [dialog, openDialog] = useLocationState('confirm-password-dialog', Dialog);

    const {
        fields,
        onSubmit: handleSubmit,
        isSubmitting,
    } = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        async submit() {
            const {problem, value} = await mutateAsync({password: form.fields.password.value}).catch(e => {
                dispatch(GenericErrorMsg.open());
                throw e;
            });
            if (problem) {
                switch (problem.error_code) {
                    case AttachWalletErrorCode.passwordMismatch: {
                        openDialog('invalid-password');
                        return;
                    }
                    case AttachWalletErrorCode.validationFailed: {
                        return problem.issues;
                    }
                }
            }
            setReceivedStatus(value.status);
            openDialog('wallets-created');
            return true;
        },
    });

    const handleForgotPassword = () => {
        navigate('/forgot-password');
    };

    return (
        <>
            <form onSubmit={handleSubmit} className='flex flex-1 flex-col gap-8'>
                <div className={'flex flex-1 flex-col gap-6'}>
                    <small className={'fv-text-l-regular pt-2 text-neutral-90'}>
                        Please enter your password to create virtual accounts.
                    </small>
                    <RsnInput.Password label={<RsnInput.Label>Password</RsnInput.Label>} {...fields.password} />
                </div>
                <div className={'flex flex-col gap-2'}>
                    <RsnTextButton.Block onClick={handleForgotPassword}>Forgot password</RsnTextButton.Block>
                    <RsnButton.Primary type={'submit'} disabled={isSubmitting}>
                        Confirm
                    </RsnButton.Primary>
                </div>
            </form>
            <RsnDialog.Controller onClose={() => navigate(-1)}>
                {(() => {
                    switch (true) {
                        case dialog === 'wallets-created' && !!receivedStatus: {
                            return (
                                <WalletsCreatedDialog
                                    onClose={() => {
                                        appLocalStorage.authStatus.set(receivedStatus);
                                        appSessionStorage.authStatus.set(receivedStatus);
                                        setReceivedStatus(null);
                                        dispatch(sessionUpdated());
                                    }}
                                />
                            );
                        }
                        case dialog === 'invalid-password': {
                            return <InvalidPasswordDialog />;
                        }
                    }
                })()}
            </RsnDialog.Controller>
        </>
    );
}

const Dialog = Schema.Literal('wallets-created', 'invalid-password');

export type ConfirmPasswordValued = {
    password: string;
};
